import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n/translator';
import { useSocketStore } from '../store/socket.store';

type Kind = 'success' | 'error' | 'warning' | 'info';
type Notice = { id: string; type: Kind; message: string; onDismiss?: () => void };
type Context = {
  showToast: (type: Kind, message: string, duration?: number, options?: { id?: string; onDismiss?: () => void }) => string;
  removeToast: (id: string) => void;
};
const ToastContext = createContext<Context | null>(null);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw Error('ToastProvider gerekli.');
  return context;
}
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Notice[]>([]);
  const queue = useSocketStore((state) => state.toastQueue);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const removeToast = useCallback((id: string) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems((current) => current.filter((notice) => notice.id !== id));
  }, []);
  const showToast = useCallback<Context['showToast']>((type, message, duration = 6000, options = {}) => {
    const id = options.id || crypto.randomUUID();
    removeToast(id);
    setItems((current) => [...current, { id, type, message, onDismiss: options.onDismiss }]);
    if (duration > 0) timers.current.set(id, setTimeout(() => removeToast(id), duration));
    return id;
  }, [removeToast]);
  useEffect(() => {
    for (const notice of queue) {
      showToast(notice.type, notice.message, notice.duration, { id: notice.id });
      useSocketStore.getState().removeQueuedToast(notice.id);
    }
  }, [queue, showToast]);
  useEffect(() => () => {
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
  }, []);
  return <ToastContext.Provider value={{ showToast, removeToast }}>
    {children}
    {createPortal(<div className="toast-stack" aria-live="polite">
      {items.map((notice) => <div key={notice.id} className={'toast ' + notice.type} role={notice.type === 'error' ? 'alert' : 'status'}>
        <span>{notice.message}</span>
        <button type="button" aria-label={t(2438)} onClick={() => { removeToast(notice.id); notice.onDismiss?.(); }}>×</button>
      </div>)}
    </div>, document.body)}
  </ToastContext.Provider>;
}
